import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getJourneyData } from '../../api/journeyService';
import { setStoredApplicationNumber, setStoredCaseId } from '../utils';
import { logError } from '../utils/logger';
import { hydrateStoreFromJourney } from '../utils/journeyResume';
import { decryptJourneyQueryValue, encryptJourneyQueryValue } from '../utils/journeyEncryption';

/**
 * When user lands on journey page with fromPaymentReturn=1 and applicationNumber,
 * hydrate the journey store from getJourneyData so Verification/Summary can render with data.
 * Returns { isResuming, isReady } so the component can show loading until hydration is done.
 */
export function usePaymentReturnResume() {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const fromPaymentReturn = searchParams.get('fromPaymentReturn');
  const applicationNumberCipher = searchParams.get('applicationNumber')?.trim() || null;
  const applicationNumberPlain = applicationNumberCipher
    ? (decryptJourneyQueryValue(applicationNumberCipher) || applicationNumberCipher)
    : null;
  const persistRehydrated = useSelector((s) => s._persist?.rehydrated);
  const leadFormData = useSelector((s) => s.journey?.leadFormData);
  const dateOfBirth =
    searchParams.get('dateOfBirth')?.trim() ||
    leadFormData?.dateOfBirth?.trim?.() ||
    null;

  const [isResuming, setIsResuming] = useState(false);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (fromPaymentReturn !== '1' || !applicationNumberPlain) {
      setIsReady(true);
      return;
    }
    if (persistRehydrated === false) {
      return;
    }
    // Always refetch from backend on return from the payment gateway (rather than trusting
    // whatever was last held in memory/redux-persist) so Summary/Verification reflect the
    // latest saved transaction status and quote summary data.
    let cancelled = false;
    setIsResuming(true);
    getJourneyData(applicationNumberPlain, dateOfBirth)
      .then((journey) => {
        if (cancelled || !journey) return;
        const appNum = journey.applicationNumber || journey.application_number || applicationNumberPlain;
        const caseId = journey.caseId || journey.c_caseId || journey.case_number || null;
        hydrateStoreFromJourney(dispatch, journey, applicationNumberPlain);
        if (appNum) setStoredApplicationNumber(appNum);
        if (caseId) setStoredCaseId(caseId);
      })
      .catch((err) => {
        logError('[usePaymentReturnResume] Failed to load journey data', err);
      })
      .finally(() => {
        if (!cancelled) {
          setIsResuming(false);
          setIsReady(true);
        }
      });
    return () => { cancelled = true; };
  }, [
    fromPaymentReturn,
    applicationNumberPlain,
    dispatch,
    persistRehydrated,
    leadFormData,
    dateOfBirth,
  ]);

  // After resume, clean URL but KEEP `applicationNumber`.
  // If we remove it completely, a browser refresh can't resume the correct stage and may fall back to /quote.
  useEffect(() => {
    if (!isReady || fromPaymentReturn !== '1' || !applicationNumberPlain) return;
    if (typeof window !== 'undefined' && window.history?.replaceState) {
      const encrypted =
        encryptJourneyQueryValue(applicationNumberPlain) || applicationNumberPlain;
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('applicationNumber', encrypted);
        // Prevent re-running resume logic on every refresh.
        url.searchParams.delete('fromPaymentReturn');
        url.searchParams.delete('status');
        url.searchParams.delete('targetStage');
        url.searchParams.delete('paymentFlowMode');

        const nextSearch = url.searchParams.toString();
        const nextUrl = nextSearch ? `${url.pathname}?${nextSearch}` : url.pathname;
        window.history.replaceState(null, document.title || '', nextUrl);
      } catch {
        // Fallback: keep only applicationNumber
        const p = window.location.pathname || '/retirement-plan';
        window.history.replaceState(
          null,
          document.title || '',
          `${p}?applicationNumber=${encodeURIComponent(encrypted)}`,
        );
      }
    }
  }, [isReady, fromPaymentReturn, applicationNumberPlain]);

  return { isResuming, isReady };
}
